import { useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { ProductImage } from "../../components/ProductImage";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Star, RefreshCw, Trash2, Upload } from "lucide-react";

const MAX = 8;

const PhotoTile = ({ img, i, count, busy, onMove, onPrimary, onReplace, onDelete }) => (
  <div className="rounded-2xl border border-[#A8B59A]/20 overflow-hidden bg-white" data-testid={`photo-tile-${i}`}>
    <div className="relative">
      <ProductImage imageId={img.id} variant="card" alt={`Photo ${i + 1}`} iconSize={24} />
      {i === 0 && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-[#59394F] text-white rounded-full px-2 py-1" data-testid="photo-primary-badge">Primary</span>}
    </div>
    <div className="flex items-center justify-between p-2">
      <button disabled={busy || i === 0} onClick={() => onMove(i, -1)} className="p-1.5 disabled:opacity-30" title="Move left" data-testid={`photo-left-${i}`}><ArrowLeft size={14} /></button>
      <button disabled={busy || i === 0} onClick={() => onPrimary(i)} className="p-1.5 disabled:opacity-30" title="Make primary" data-testid={`photo-primary-${i}`}><Star size={14} /></button>
      <label className={`p-1.5 cursor-pointer ${busy ? "opacity-30 pointer-events-none" : ""}`} title="Replace">
        <RefreshCw size={14} />
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`photo-replace-${i}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onReplace(img.id, f); }} />
      </label>
      <button disabled={busy || i === count - 1} onClick={() => onMove(i, 1)} className="p-1.5 disabled:opacity-30" title="Move right" data-testid={`photo-right-${i}`}><ArrowRight size={14} /></button>
      <button disabled={busy} onClick={() => onDelete(img.id)} className="p-1.5 text-[#A84E35] disabled:opacity-30" title="Delete" data-testid={`photo-delete-${i}`}><Trash2 size={14} /></button>
    </div>
  </div>
);

export const ProductPhotos = ({ product, onClose, onChanged }) => {
  const { getToken } = useAuth();
  const [images, setImages] = useState(product.images || []);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const base = `/admin/products/${product.id}/images`;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${await getToken()}` };
      const res = await fn(headers);
      setImages(res.data.images || []);
      onChanged();
      toast.success(ok);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — please try again.");
    }
    setBusy(false);
  };

  const upload = (files) => {
    if (!files.length) return;
    if (images.length + files.length > MAX) return toast.error(`A product can have at most ${MAX} photos.`);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    run((headers) => api.post(base, fd, { headers }), files.length > 1 ? "Photos uploaded" : "Photo uploaded");
  };
  const order = (ids, ok) => run((headers) => api.put(`${base}/order`, { image_ids: ids }, { headers }), ok);
  const move = (i, dir) => { const ids = images.map((x) => x.id); [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]]; order(ids, "Order saved"); };
  const primary = (i) => { const ids = images.map((x) => x.id); order([ids[i], ...ids.filter((_, j) => j !== i)], "Primary photo set"); };
  const replace = (iid, f) => { const fd = new FormData(); fd.append("file", f); run((headers) => api.post(`${base}/${iid}/replace`, fd, { headers }), "Photo replaced"); };
  const remove = (iid) => { if (window.confirm("Delete this photo?")) run((headers) => api.delete(`${base}/${iid}`, { headers }), "Photo deleted"); };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#F4EBDD] max-h-[90vh] overflow-y-auto" data-testid="photos-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Photos · {product.name}</DialogTitle>
          <DialogDescription>The first photo is the primary image on the shop, product page and cart. Photos are centred on a warm oat-cream square automatically — nothing is stretched or cropped.</DialogDescription>
        </DialogHeader>
        {images.length === 0 ? (
          <p className="text-sm text-[#5E4D5A] py-6" data-testid="photos-empty">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <PhotoTile key={img.id} img={img} i={i} count={images.length} busy={busy} onMove={move} onPrimary={primary} onReplace={replace} onDelete={remove} />
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="photos-file-input" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; upload(fs); }} />
          <Button disabled={busy || images.length >= MAX} onClick={() => fileRef.current?.click()} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white transition-colors" data-testid="photos-upload-btn">
            <Upload size={14} className="mr-2" />{busy ? "Working…" : "Upload photos"}
          </Button>
          <span className="text-xs text-[#5E4D5A]">{images.length}/{MAX} · JPG, PNG or WebP · up to 15 MB · at least 300px</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
